import { PageWrapper, OvFormProvider, OvFormContext } from '@components'
import { INVOICE_DEFAULT_VALUES, LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { useIsMobile } from '@hooks/useIsMobile'
import { useStore } from '@hooks/useStore'
import { Save } from '@mui/icons-material'
import { Box, Button, Tooltip } from '@mui/material'
import { ISingleDocument } from '@typings'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { FieldValues, useForm, UseFormReturn } from 'react-hook-form'
import { generatePath, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { UpdateInvoiceForm } from 'src/partials/index.partials'

const InvoiceCreateContent = observer(({ form }: { form: UseFormReturn<any> }) => {
  const { ui, invoice, shared, client, company } = useStore()
  const { setAllowNavigation, allowNavigation, allowNavigationRef } = useContext(OvFormContext)
  const isMobile = useIsMobile()
  const pendingNavigationRef = useRef<string | null>(null)
  const navigate = useNavigate()

  const handleBack = useCallback(() => {
    // Let the navigation blocker handle the confirmation
    navigate(URL_PATHS.INVOICE_LIST)
  }, [navigate])

  const handleSuccess = useCallback(
    (data: Partial<ISingleDocument>) => {
      const route = generatePath(URL_PATHS.INVOICE_DETAIL, { id: data.id })
      ui.handleToaster('Factuur aangemaakt', { type: 'success' })

      // Set ref DIRECTLY and IMMEDIATELY
      if (allowNavigationRef) {
        allowNavigationRef.current = true
      }

      // Store the route and trigger navigation
      pendingNavigationRef.current = route
      setAllowNavigation?.(true)
    },
    [setAllowNavigation, ui, allowNavigationRef]
  )

  // Navigate when allowNavigation becomes true
  useEffect(() => {
    if (allowNavigation && pendingNavigationRef.current) {
      const route = pendingNavigationRef.current
      pendingNavigationRef.current = null
      navigate(route)
    }
  }, [allowNavigation, navigate])

  const handleErrors = (errors: Record<string, string[]>, message?: string) => {
    form.clearErrors()
    const errorKeys = Object.keys(errors)

    errorKeys.map(singleKey =>
      form.setError(singleKey as keyof typeof INVOICE_DEFAULT_VALUES, {
        type: 'custom',
        message: errors[singleKey][0],
      })
    )

    if (message) toast(message, { type: 'error' })
  }

  const onSubmitCallback = async (values: FieldValues) => {
    try {
      const res = await invoice.create(values)
      handleSuccess(res)
    } catch (e: any) {
      handleErrors(e.errors, e.message)
    }
  }

  const handleSubmit = () => {
    form.handleSubmit(onSubmitCallback)()
  }

  useEffect(() => {
    invoice.reset(true)
    client.getAll()
    if (!shared.has_vat_codes) shared.getVatCodes()
    // Ensure company data is loaded for VAT code filtering
    if (!company.current_company) company.getMyCompany()
  }, [])

  return (
    <PageWrapper
      backAction={handleBack}
      pageTitle="Nieuwe Factuur"
      actions={
        <Box
          display="flex"
          gap={1}
          width={isMobile ? '100%' : 'auto'}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleBack}
            sx={{ flex: isMobile ? 1 : 'unset' }}>
            {LABELS.CANCEL}
          </Button>
          <Tooltip title={TOOLTIPS.SAVE_INVOICE}>
            <Button
              size="small"
              endIcon={<Save />}
              variant="contained"
              onClick={handleSubmit}
              sx={{ flex: isMobile ? 1 : 'unset' }}>
              {LABELS.INVOICE_SAVE}
            </Button>
          </Tooltip>
        </Box>
      }
      fixedActions={true}>
      <UpdateInvoiceForm documentType="invoice" />
    </PageWrapper>
  )
})

export const InvoiceCreateView = observer(() => {
  const { invoice } = useStore()
  const form = useForm({
    mode: 'onChange',
    defaultValues: INVOICE_DEFAULT_VALUES,
  })

  // Subscribe to form state to enable dirty tracking
  // React-hook-form's formState is lazily evaluated - we need to subscribe to it
  React.useEffect(() => {
    const subscription = form.watch(() => {
      // Access formState to trigger tracking
      form.formState.isDirty
    })
    return () => subscription.unsubscribe()
  }, [form])

  const handleSubmit = () => {
    // This will be handled by the inner component
  }

  return (
    <OvFormProvider
      loading={invoice.loading}
      form={form}
      onSubmit={handleSubmit}>
      <InvoiceCreateContent form={form} />
    </OvFormProvider>
  )
})
